import React, { ReactElement, useCallback, useContext, useEffect, useState } from 'react'
import styled from 'styled-components'
import _ from 'lodash'
import i18n from 'live/services/i18n'
import authorization from 'live/services/authorization'
import { StaticDataType, STATIC_DATA_STATES } from '../chart-components'
import { CustomModalsProps, ModalTabSections } from '../modal-components'
import { ModalHeader, ModalBody, ModalFooter, ModalButton } from '../modal-components-style'
import { createSingleEntity, RIG_TABLE_API, updateSingleEntity } from '../../api/api'
import { StaticDataContext, useStaticDataWidget } from '../../context/StaticDataContext'
import { RawRigTableEvent } from '../../utils/type'
import RigTableTab from './RigTableTab'
import { emptyRawRigTableEvent } from './types'
import { useRequest } from 'ahooks'
import { fetchRigTableTimestamps } from '../../api/rigTableApi'

interface RigTableModalProps extends CustomModalsProps {
    event: RawRigTableEvent
    closeModal: (rigTableData?: any, action?: string) => void
}

// TODO - To properly work, require rig information.
export const RigTableModal = ({
    title = StaticDataType.RIG_TABLE,
    event,
    closeModal
}: RigTableModalProps): ReactElement => {
    const { reloadData } = useStaticDataWidget()
    const { liveTheme } = useContext(StaticDataContext)

    const hasPermissionToEdit =
        authorization.superAdmin ||
        authorization.admin ||
        authorization.Permission.ADMIN ||
        authorization.Permission.OGPERFORMANCE_MANAGE_KPI ||
        authorization.Permission.OGPERFORMANCE_ADMINISTRATE_KPI

    const [rawRigTableEvent, setRawRigTableEvent] = useState<RawRigTableEvent>(
        event?.id ? event : { ...emptyRawRigTableEvent, ...event }
    )

    const { data: result } = useRequest(() => fetchRigTableTimestamps(event))

    const updateRigTableEvent = useCallback((event: RawRigTableEvent) => {
        setRawRigTableEvent(event)
    }, [])

    const postRigTable = (rawRigTable: RawRigTableEvent): any => {
        const rigTableData = (({ state, ...rawRigTable }) => rawRigTable)(rawRigTable)
        return (rawRigTable.id ? updateSingleEntity : createSingleEntity)(RIG_TABLE_API, rigTableData)
    }

    const [selectedOptionWarning, setSelectedOptionWarning] = useState(false)

    useEffect(() => {
        if (result?.start) setRawRigTableEvent(prevstate => ({ ...prevstate, start: result.start }))
    }, [result?.end, result?.start])

    const btn =
        event.state != STATIC_DATA_STATES.EMPTY
            ? event.state !== STATIC_DATA_STATES.FINISHED
                ? 'Finish'
                : 'Save'
            : 'Start'

    return (
        <>
            <RigTableModalContainer>
                <ModalHeader>{i18n(title).toUpperCase()}</ModalHeader>
                <ModalBody liveTheme={liveTheme}>
                    <ModalTabSections
                        selected
                        content={
                            <RigTableTab
                                event={rawRigTableEvent}
                                updateRigTableEvent={updateRigTableEvent}
                                hasPermissionToEdit={hasPermissionToEdit}
                                selectedOptionWarning={selectedOptionWarning}
                                setSelectedOptionWarning={setSelectedOptionWarning}
                            ></RigTableTab>
                        }
                    />
                </ModalBody>
                <ModalFooter liveTheme={liveTheme}>
                    {hasPermissionToEdit && (
                        <ModalButton
                            styleType="action"
                            onClick={() => {
                                if (!rawRigTableEvent.status) {
                                    setSelectedOptionWarning(true)

                                    return
                                }
                                setSelectedOptionWarning(false)
                                postRigTable(rawRigTableEvent).then(() => {
                                    closeModal()
                                    reloadData()
                                })
                            }}
                        >
                            {i18n(btn)}
                        </ModalButton>
                    )}
                    <ModalButton styleType="cancel" onClick={closeModal}>
                        {hasPermissionToEdit ? i18n('Cancel') : i18n('Close')}
                    </ModalButton>
                </ModalFooter>
            </RigTableModalContainer>
        </>
    )
}

const RigTableModalContainer = styled.div`
    height: 100%;
    display: flex;
    flex-flow: column;
    justify-content: space-between;

    input::placeholder {
        color: #1d1d1d;
    }

    input[type='text']:disabled {
        background: #eeeeee;
        background-color: #eeeeee;
    }

    .input-normal {
        textarea {
            height: 115px;
            border-bottom-color: 1px solid ${({ theme }) => theme['input-borders']};
            border: 1px solid ${({ theme }) => theme['input-borders']};
            color: ${({ theme }) => theme['text-color']};
        }
    }
`
